import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, doc, updateDoc, deleteDoc, Timestamp, getDoc, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useBranchContext } from '@/contexts/BranchContext';
import { useAuditLog } from './useAuditLog';
import type { User, UserRole } from '@/types';

/**
 * Hook for managing users in the system.
 * Handles fetching, filtering by branch, role updates, and deletion.
 * Supports both real Firestore data and Demo mode mock data.
 * 
 * @returns Object containing user data, loading state, and management functions.
 */
export const useUsers = () => {
    const { selectedBranch } = useBranchContext();
    const { logAction } = useAuditLog();
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    /**
     * Fetches all users from Firestore or returns mock data in demo mode.
     */
    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Demo mode: Use mock data

            // Real Firebase mode
            const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            const fetchedUsers: User[] = [];

            // Helper to safely convert Firestore timestamp or string to Date
            const toDate = (val: Timestamp | Date | string | number | null | undefined): Date => {
                if (!val) return new Date();
                if (val instanceof Timestamp) return val.toDate();
                if (val instanceof Date) return val;
                return new Date(val);
            };

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                fetchedUsers.push({
                    uid: doc.id,
                    email: data.email,
                    displayName: data.displayName,
                    photoURL: data.photoURL,
                    role: data.role,
                    roleId: data.roleId, // Fetch roleId
                    roleIds: data.roleIds, // Fetch roleIds
                    status: data.status || 'active', // Default validation
                    branchId: data.branchId,
                    subscriptionStatus: data.subscriptionStatus,
                    activeSubscriptionId: data.activeSubscriptionId,
                    isKidProfile: data.isKidProfile,
                    age: data.age,
                    parentId: data.parentId,
                    referralCode: data.referralCode,
                    // Denormalized list of kid UIDs on the parent doc (Jun 1
                    // 2026 backfill + maintained going forward by every
                    // kid-creation flow). Used by WalletBalanceDisplay to
                    // render a parent row as the family total — sum of kid
                    // wallet balances — instead of the parent's own wallet
                    // doc balance (which is always 0 in this business model).
                    // Without this field the family-aggregation code falls
                    // back to total=0.
                    childrenIds: data.childrenIds,
                    // isStudent multi-role flag. Used in tandem with isParent
                    // to detect the dual-role case (parent who is ALSO
                    // enrolled as a student) so WalletBalanceDisplay can show
                    // both own and family balances. Without this field the
                    // alsoStudent branch never triggers and parents who
                    // happen to be students get only the family number
                    // (which is still wrong by less than the dual-role case).
                    isStudent: data.isStudent,
                    isParent: data.isParent,
                    isCoach: data.isCoach,
                    isAdmin: data.isAdmin,
                    mobile: data.mobile,
                    totalScore: data.totalScore || 0, // Ensure defaulting
                    belt: data.belt, // Belt rank for avatar display
                    stripes: data.stripes, // Belt stripes
                    createdAt: toDate(data.createdAt),
                    updatedAt: toDate(data.updatedAt),
                } as User);
            });

            setAllUsers(fetchedUsers);
            setError(null);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    // Filter users by selected branch
    useEffect(() => {
        if (selectedBranch) {
            // Filter by selected branch, but ALWAYS show users with no branch assigned (so they can be managed)
            setUsers(allUsers.filter(u => u.branchId === selectedBranch || !u.branchId));
        } else {
            // Show all users (for super admin with "All Branches" selected)
            setUsers(allUsers);
        }
    }, [selectedBranch, allUsers]);

    useEffect(() => {
        fetchUsers();
    }, []);

    /**
     * Updates a user's role.
     * @param userId The UID of the user to update.
     * @param newRole The new role to assign.
     */
    const updateUserRole = async (userId: string, newRole: UserRole) => {
        try {
            // Demo mode: Just update local state

            // Real Firebase mode
            await updateDoc(doc(db, 'users', userId), {
                role: newRole,
                updatedAt: new Date(),
            });

            await logAction({
                action: 'role_change',
                module: 'Users',
                targetId: userId,
                details: `Role changed to ${newRole}`
            });

            // Update local state
            setAllUsers(allUsers.map(user =>
                user.uid === userId ? { ...user, role: newRole } : user
            ));

            return true;
        } catch (err) {
            console.error('Error updating user role:', err);
            throw err;
        }
    };

    /**
     * Permanently deletes a user and their associated profile documents.
     * Note: This deletes Firestore data only, not the Authentication record.
     * @param userId The UID of the user to delete.
     */
    const deleteUser = async (userId: string) => {
        try {
            // Demo mode: Just update local state

            // Real Firebase mode
            // Note: This only deletes the Firestore document. 
            // Deleting the Auth user requires Cloud Functions or doing it from the client 
            // if the current user has permissions (which is tricky for other users).
            // For Phase 2, we'll just delete the Firestore record which "deactivates" them in our app.
            await deleteDoc(doc(db, 'users', userId));

            // Also delete profile documents if they exist
            try { await deleteDoc(doc(db, 'studentProfiles', userId)); } catch { /* ignore */ }
            try { await deleteDoc(doc(db, 'coachProfiles', userId)); } catch { /* ignore */ }

            await logAction({
                action: 'user_delete',
                module: 'Users',
                targetId: userId,
                details: 'User and profiles deleted'
            });

            setAllUsers(allUsers.filter(user => user.uid !== userId));
            return true;
        } catch (err) {
            console.error('Error deleting user:', err);
            throw err;
        }
    };

    /**
     * Deletes multiple users at once.
     * @param userIds Array of UIDs to delete.
     */
    const deleteUsers = async (userIds: string[]) => {
        try {

            await Promise.all(userIds.map(async (userId) => {
                await deleteDoc(doc(db, 'users', userId));
                try { await deleteDoc(doc(db, 'studentProfiles', userId)); } catch { /* ignore */ }
                try { await deleteDoc(doc(db, 'coachProfiles', userId)); } catch { /* ignore */ }
            }));

            setAllUsers(prev => prev.filter(u => !userIds.includes(u.uid)));
            return true;
        } catch (err) {
            console.error('Error deleting users:', err);
            throw err;
        }
    };


    /**
     * Updates a user's account status (e.g., approve or reject).
     * When approving, also auto-enrolls user in their preferred class if set during onboarding.
     * @param userId The UID of the user.
     * @param status The new status.
     */
    const setUserStatus = async (userId: string, status: 'active' | 'pending' | 'rejected', reason?: string) => {
        try {
            // Demo mode

            const updateData: Record<string, any> = { status, updatedAt: new Date() };
            if (status === 'rejected' && reason) {
                updateData.rejectionReason = reason;
            } else if (status === 'active') {
                // Clear rejection reason if approving
                updateData.rejectionReason = null;
            }

            // Fetch user data for logging
            const userDocSnap = await getDoc(doc(db, 'users', userId));
            const userName = userDocSnap.exists() ? userDocSnap.data().displayName : 'Unknown User';

            await updateDoc(doc(db, 'users', userId), updateData);

            // Log Action
            await logAction({
                action: status === 'active' ? 'user_approve' : status === 'rejected' ? 'user_reject' : 'user_status_change',
                module: 'Users',
                targetId: userId,
                details: `Status changed to ${status} for user: ${userName}${reason ? `. Reason: ${reason}` : ''}`
            });

            // When approving a user, check if they have a preferred class and auto-enroll them
            if (status === 'active') {
                try {
                    const userDoc = await getDoc(doc(db, 'users', userId));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const preferredClassId = userData.preferredClassId;

                        if (preferredClassId) {
                            // Get class info for enrollment
                            const classDoc = await getDoc(doc(db, 'classes', preferredClassId));
                            if (classDoc.exists()) {
                                const classData = classDoc.data();

                                // Create the enrollment
                                await addDoc(collection(db, 'enrollments'), {
                                    userId: userId,
                                    userName: userData.displayName,
                                    classId: preferredClassId,
                                    className: classData.name,
                                    enrolledAt: new Date(),
                                    status: 'active'
                                });

                                // Update class enrolled count
                                await updateDoc(doc(db, 'classes', preferredClassId), {
                                    enrolled: (classData.enrolled || 0) + 1
                                });

                                // Clear preferredClassId from user doc (enrollment complete)
                                await updateDoc(doc(db, 'users', userId), {
                                    preferredClassId: null
                                });

                                await logAction({
                                    action: 'auto_enroll',
                                    module: 'Classes',
                                    targetId: userId,
                                    details: `Auto-enrolled in class ${classData.name}`
                                });

                                console.log(`Auto-enrolled user ${userId} in class ${preferredClassId}`);
                            }
                        }
                    }
                } catch (enrollErr) {
                    console.error('Error auto-enrolling user:', enrollErr);
                    // Don't throw - approval was successful, enrollment is a bonus
                }
            }

            // Update local state
            setAllUsers(prev => prev.map(u => u.uid === userId ? { ...u, status, rejectionReason: reason } : u));
            return true;
        } catch (err) {
            console.error('Error updating user status:', err);
            throw err;
        }
    };

    // Computed: Only active users (for public-facing modules like leaderboard, likes, etc.)
    // Pending and rejected users should NOT appear in these contexts
    const activeUsers = allUsers.filter(u => u.status === 'active' || !u.status);

    return {
        users,
        allUsers, // Unfiltered list (for admin management)
        activeUsers, // Only approved/active users (for public-facing features)
        loading,
        error,
        fetchUsers,
        updateUserRole,
        deleteUser,
        deleteUsers,
        setUserStatus // Export new function
    };
};
